
import { FC, useState, useEffect } from "react";
import api from "../services/api";
import {Client} from "../context/DataContext";
import {QRCodeCanvas} from "qrcode.react";

const InviteUserModal: FC<{
    isOpen: boolean;
    onClose: () => void;
    clients: Client[];
}> = ({ isOpen, onClose, clients }) => {
    const [role, setRole] = useState('COLLABORATOR');
    const [clientId, setClientId] = useState<number | ''>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [generatedLink, setGeneratedLink] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState('');

    useEffect(() => {
        if (clientId !== '') setRole('COLLABORATOR');
    }, [clientId]);

    const handleGenerateLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const response = await api.get('/api/tokens/invite', {
                params: { role, clientId: clientId === '' ? -1 : clientId }
            });
            if (response.data.status) {
                const inviteLink = `${response.data.data}`;
                setGeneratedLink(inviteLink);
            } else {
                throw new Error(response.data.message);
            }
        } catch (err: any) {
            setError(err.message || "Errore nella generazione del link.");
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink);
            setCopySuccess('Copiato!');
            setTimeout(() => setCopySuccess(''), 2000);
        }
    };

    const handleClose = () => {
        setGeneratedLink(null);
        setRole('COLLABORATOR');
        setClientId('');
        setError('');
        setIsLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-lg">
                <div className="p-6 border-b border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Invita Nuovo Utente</h2>
                    <button onClick={handleClose} className="text-slate-400 hover:text-white text-3xl leading-none">&times;</button>
                </div>

                {generatedLink ? (
                    <div className="p-6 text-center">
                        <p className="text-slate-300 mb-4">Invito generato con successo. Condividi il link o il QR Code.</p>
                        <div className="p-6 bg-white rounded-lg inline-block">
                            <QRCodeCanvas value={generatedLink} size={192} />
                        </div>
                        <div className="flex items-center space-x-2 mt-4">
                            <input type="text" readOnly value={generatedLink} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white" />
                            <button onClick={copyToClipboard} className="px-4 py-2 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 whitespace-nowrap">
                                {copySuccess || 'Copia'}
                            </button>
                        </div>
                        <button onClick={handleClose} className="mt-6 w-full px-5 py-3 rounded-lg text-white font-semibold bg-slate-700 hover:bg-slate-600">Fatto</button>
                    </div>
                ) : (
                    <form onSubmit={handleGenerateLink} className="p-6">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="role" className="block text-sm font-medium text-slate-300 mb-2">Ruolo</label>
                                <select id="role" value={role} onChange={e => setRole(e.target.value)} disabled={clientId !== ''} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-slate-700">
                                    <option value="COLLABORATOR">Collaboratore</option>
                                    <option value="STAFF">Staff</option>
                                    <option value="ADMIN">Admin</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="client" className="block text-sm font-medium text-slate-300 mb-2">Cliente (Opzionale)</label>
                                <select id="client" value={clientId} onChange={e => setClientId(e.target.value === '' ? '' : Number(e.target.value))} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500">
                                    <option value="">Nessun Cliente</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                        {error && <p className="text-red-400 text-sm mt-4">{error}</p>}
                        <div className="mt-6">
                            <button type="submit" disabled={isLoading} className="w-full px-5 py-3 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors disabled:bg-slate-600">
                                {isLoading ? 'Generazione...' : 'Genera Link di Invito'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default InviteUserModal;
